// Dynamic entity naming (design 2026-07-14) — Phase 0 app-path cascade.
//
// Closes bug 1 (spec §1.4): `grid plug` auto-init derived its slug from
// `suggestSlug(basename(cwd))` with NO generic reject, so a folder named
// `app` / `dist` / `index` / `src` became the permanent URL slug. When the
// directory basename yields a generic (or empty) slug, this cascade produces a
// meaningful, non-generic slug instead — cheaply and deterministically, with no
// AI and no network:
//
//   1. package.json `name`      → suggestSlug
//   2. README first heading      → suggestSlug
//   3. framework label + suffix  → e.g. `next-js-app-3a8f`
//   4. typed placeholder         → `<type>-<suffix>` (never a bare generic)
//
// The first three carry real intent (`inferred-app`); only the last is a
// `fallback-generic` placeholder the brain may upgrade later.

import { existsSync, readFileSync } from 'node:fs';
import { randomBytes } from 'node:crypto';
import { join } from 'node:path';

import { suggestSlug } from '../commands/slug.js';
import {
  isGenericSlug,
  buildFallbackSlug,
  type NameSource,
} from '../../../shared/src/generic-slugs.js';
import type { FrameworkDetection } from './framework-detect.js';

export interface AppSlugResult {
  /** A valid, non-generic slug. */
  slug: string;
  /** Provenance for the create request's `slug_source`. */
  source: Extract<NameSource, 'inferred-app' | 'fallback-generic'>;
}

/** Injection seam for tests. Defaults read the real filesystem at `cwd`. */
export interface AppSlugDeps {
  readPackageName?: (cwd: string) => string | null;
  readReadmeHeading?: (cwd: string) => string | null;
  /** 4-char lowercase-hex suffix; injectable so tests are deterministic. */
  suffix?: () => string;
}

/** Strip an npm scope (`@acme/widget` → `widget`) — the scope is org noise. */
function stripNpmScope(name: string): string {
  return name.replace(/^@[^/]+\//, '');
}

/** Read package.json `name`, or null when absent/unreadable/unnamed. */
function defaultReadPackageName(cwd: string): string | null {
  const path = join(cwd, 'package.json');
  if (!existsSync(path)) return null;
  try {
    const parsed = JSON.parse(readFileSync(path, 'utf-8')) as { name?: unknown };
    const name = typeof parsed.name === 'string' ? parsed.name : null;
    return name && name.trim() !== '' ? name : null;
  } catch {
    return null;
  }
}

/** Read the first Markdown `#`–`###` heading of README(.md), first 4 KiB. */
function defaultReadReadmeHeading(cwd: string): string | null {
  for (const name of ['README.md', 'readme.md', 'README', 'Readme.md']) {
    const path = join(cwd, name);
    if (!existsSync(path)) continue;
    try {
      const head = readFileSync(path, 'utf-8').slice(0, 4096);
      for (const line of head.split(/\r?\n/)) {
        const m = line.match(/^#{1,3}\s+(.+)$/);
        if (m) return m[1].trim();
        if (line.trim() && !line.startsWith('#')) break; // doc doesn't open with a heading
      }
    } catch {
      /* unreadable — try the next candidate */
    }
  }
  return null;
}

function defaultSuffix(): string {
  return randomBytes(2).toString('hex');
}

/** A candidate is usable iff it slugifies to a valid, non-generic slug. */
function usable(raw: string | null): string | null {
  if (!raw) return null;
  const slug = suggestSlug(raw);
  if (slug && !isGenericSlug(slug)) return slug;
  return null;
}

/**
 * Produce a meaningful, non-generic slug for an app whose directory basename
 * gave a generic/empty one. Always returns a valid slug — the typed placeholder
 * is the guaranteed floor, so this never fails.
 */
export function cascadeAppSlug(
  cwd: string,
  detection: FrameworkDetection | null,
  deps: AppSlugDeps = {},
): AppSlugResult {
  const readPackageName = deps.readPackageName ?? defaultReadPackageName;
  const readReadmeHeading = deps.readReadmeHeading ?? defaultReadReadmeHeading;
  const suffix = deps.suffix ?? defaultSuffix;

  // 1. package.json name (scope-stripped)
  const pkgName = readPackageName(cwd);
  const fromPkg = usable(pkgName ? stripNpmScope(pkgName) : null);
  if (fromPkg) return { slug: fromPkg, source: 'inferred-app' };

  // 2. README first heading
  const fromReadme = usable(readReadmeHeading(cwd));
  if (fromReadme) return { slug: fromReadme, source: 'inferred-app' };

  // 3. framework label + suffix (e.g. `next-js-app-3a8f`) — meaningful, never bare
  if (detection?.label) {
    const labelSlug = suggestSlug(detection.label);
    if (labelSlug) return { slug: buildFallbackSlug(labelSlug, suffix()), source: 'inferred-app' };
  }

  // 4. typed placeholder — `<type>-<suffix>`, guaranteed non-generic
  const typePrefix = detection?.type ?? 'app';
  return { slug: buildFallbackSlug(typePrefix, suffix()), source: 'fallback-generic' };
}

// ── Naming-inference-first (2026-07-14) — signal gathering for the server ──
//
// The cascade above settles the create-time slug locally (a fast pre-fill). But
// a DEFAULT/placeholder slug (`app-3a8f`, or a generic basename) is not a good
// human name. So the CLI also forwards the raw semantic signals it already reads
// here to the server, which runs one cheap AI pass to CRAFT a human-friendly
// slug + title (`inferEntityName`). These are the same files the cascade opens —
// gathering them costs one extra small read of package.json + README.

/** Bounded semantic signals forwarded on the create request for inference. */
export interface AppSignals {
  packageName: string | null;
  packageDescription: string | null;
  readmeHead: string | null;
  frameworkLabel: string | null;
}

/** Read package.json `description`, or null. */
function defaultReadPackageDescription(cwd: string): string | null {
  const path = join(cwd, 'package.json');
  if (!existsSync(path)) return null;
  try {
    const parsed = JSON.parse(readFileSync(path, 'utf-8')) as { description?: unknown };
    const d = typeof parsed.description === 'string' ? parsed.description.trim() : '';
    return d !== '' ? d.slice(0, 500) : null;
  } catch {
    return null;
  }
}

/** Read the README's first heading + first paragraph (bounded ~1 KiB). */
function defaultReadReadmeHead(cwd: string): string | null {
  for (const name of ['README.md', 'readme.md', 'README', 'Readme.md']) {
    const path = join(cwd, name);
    if (!existsSync(path)) continue;
    try {
      const head = readFileSync(path, 'utf-8').slice(0, 2048).trim();
      return head !== '' ? head.slice(0, 1024) : null;
    } catch {
      /* unreadable — try the next candidate */
    }
  }
  return null;
}

export interface AppSignalsDeps {
  readPackageName?: (cwd: string) => string | null;
  readPackageDescription?: (cwd: string) => string | null;
  readReadmeHead?: (cwd: string) => string | null;
}

/**
 * Gather the bounded signals the server's naming inference reads. All optional;
 * a directory with no package.json / README yields all-null (the server then
 * skips inference — its `hasReadableSignal` is false — and the deterministic
 * placeholder stands). `frameworkLabel` comes from the already-computed
 * detection, so no extra work.
 */
export function gatherAppSignals(
  cwd: string,
  detection: FrameworkDetection | null,
  deps: AppSignalsDeps = {},
): AppSignals {
  const readPackageName = deps.readPackageName ?? defaultReadPackageName;
  const readPackageDescription = deps.readPackageDescription ?? defaultReadPackageDescription;
  const readReadmeHead = deps.readReadmeHead ?? defaultReadReadmeHead;
  const pkgName = readPackageName(cwd);
  return {
    packageName: pkgName ? stripNpmScope(pkgName) : null,
    packageDescription: readPackageDescription(cwd),
    readmeHead: readReadmeHead(cwd),
    frameworkLabel: detection?.label ?? null,
  };
}
